<template>
    <AllocationSelectionPanel
        :count-allocation-variants="countTotalVariants"
        :type="type"
        @make-main-variants-visible="$emit('makeMainVariantVisible')"
        @make-active-variants-visible="$emit('makeActiveVariantsVisible')"
        @close="$emit('close')"
    >
        <AllocationSelectionPanelEmpty v-if="sections.length === 0" />
        <AllocationGroupSection
            v-for="section in sections"
            v-else
            :key="section.title"
            :title="section.title"
            :link-target="section.linkTarget"
            :is-expanded="section.isExpanded"
            :status-color="section.statusColor"
            :status-label="section.statusLabel"
            :count-visible="section.activeVariants.filter((allocation) => allocation.visibility === 'Visible').length"
            :count-total="getCountTotal(section)"
            @toggle-is-expanded="$emit('toggleExpanded', section.id)"
        >
            <template #overline>
                <p v-if="section.category && section.nationalString" class="text-sm">
                    {{ getSectionCategoryString(section.category) }} <span class="text-gray-300">| </span
                    >{{ section.nationalString }}
                </p>
            </template>
            <AllocationVariantListEmpty v-if="section.activeVariants.length === 0" />
            <AllocationVariantList v-else>
                <AllocationVariantListEntry
                    v-for="variant in section.activeVariants"
                    :key="variant.index"
                    :allocation-variant-index="variant.index"
                    :title="variant.title"
                    :link-target="variant.linkTarget"
                    :status-color="variant.statusColor"
                    :status-label="variant.statusLabel"
                    :visibility="variant.visibility"
                    :is-main-variant="variant.isMainVariant"
                    :category-color="defineCategoryColor(section.category)"
                    @toggle-visibility="(v) => $emit('toggleVisibility', v, section.id, variant.id)"
                >
                    <AllocationVariantVisibilityOverride
                        v-if="variant.hasInvisibleAllocations"
                        :visibility="variant.overrideVisibility ?? 'Invisible'"
                        @toggle-visibility="(v) => $emit('toggleOverrideVisibility', v, section.id, variant.id)"
                    />
                </AllocationVariantListEntry>
            </AllocationVariantList>

            <ExpandableSection
                v-if="section.nonActiveVariants.length > 0"
                :is-expanded="section.areNonActiveVariantsExpanded"
                class="mt-1"
                border-class="mx-6"
                @toggle-is-expanded="$emit('toggleNonActiveVariants', section.id)"
            >
                <template #label>
                    <template v-if="section.areNonActiveVariantsExpanded">
                        {{ $t('collapse-section') }}
                    </template>
                    <template v-else>
                        {{ $t('expand-section-to-all-allocation-variants') }}
                    </template>
                </template>

                <template #content>
                    <AllocationVariantList>
                        <AllocationVariantListEntry
                            v-for="variant in section.nonActiveVariants"
                            :key="variant.index"
                            :allocation-variant-index="variant.index"
                            :title="variant.title"
                            :link-target="variant.linkTarget"
                            :status-color="variant.statusColor"
                            :status-label="variant.statusLabel"
                            :visibility="variant.visibility"
                            :is-main-variant="variant.isMainVariant"
                            :category-color="defineCategoryColor(section.category)"
                            @toggle-visibility="(v) => $emit('toggleVisibility', v, section.id, variant.id)"
                        />
                    </AllocationVariantList>
                </template>

                <template #tooltip-expanded>{{ $t('collapse-non-active-allocation-variants') }}</template>

                <template #tooltip-collapsed>{{ $t('expand-to-all-allocation-variants') }}</template>
            </ExpandableSection>
        </AllocationGroupSection>
    </AllocationSelectionPanel>
</template>
<script setup lang="ts">
import AllocationGroupSection from '@/components/AllocationSelectionPanel/AllocationGroupSection.vue';
import { AllocationGroupType } from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.types';
import AllocationSelectionPanel from '@/components/AllocationSelectionPanel/AllocationSelectionPanel.vue';
import AllocationSelectionPanelEmpty from '@/components/AllocationSelectionPanel/AllocationSelectionPanelEmpty.vue';
import AllocationVariantList from '@/components/AllocationSelectionPanel/AllocationVariantList.vue';
import AllocationVariantListEmpty from '@/components/AllocationSelectionPanel/AllocationVariantListEmpty.vue';
import AllocationVariantListEntry from '@/components/AllocationSelectionPanel/AllocationVariantListEntry.vue';
import AllocationVariantVisibilityOverride, {
    AllocationVariantOverrideVisibility,
} from '@/components/AllocationSelectionPanel/AllocationVariantVisibilityOverride.vue';
import { BadgeVariant } from '@/components/Badge/Badge.vue';
import ExpandableSection from '@/components/ExpandableSection/ExpandableSection.vue';
import { getCategoryColor } from '@/external_event/externalEvent';
import { ExternalEventSeriesCategory } from '@/generated/graphql';
import { useFluent } from 'fluent-vue';
import { computed } from 'vue';
import { RouteLocationRaw } from 'vue-router';
import { VariantStateVisibility } from './AllocationSelectionFilter.state';
import { VISIBILITY_TOGGLE_OPTIONS } from './composables/useDesignerFilterData';

export type VariantVisibility = 'Visible' | 'Invisible' | 'Outside-Timeline-Range' | 'No-Allocations';

export type Variant = {
    id: string;
    index: number;
    title: string;
    linkTarget: RouteLocationRaw;
    statusColor: BadgeVariant;
    status: string;
    statusLabel: string;
    isMainVariant: boolean;
    visibility: VariantVisibility;
    hasInvisibleAllocations: boolean;
    overrideVisibility?: AllocationVariantOverrideVisibility;
};

export type Section = {
    id: string;
    title: string;
    linkTarget: RouteLocationRaw;
    status: string;
    statusColor: BadgeVariant;
    statusLabel: string;
    isExpanded: boolean;
    areNonActiveVariantsExpanded: boolean;
    activeVariants: Variant[];
    nonActiveVariants: Variant[];
    category?: ExternalEventSeriesCategory;
    nationalString?: string;
};

const props = defineProps<{
    type: AllocationGroupType;
    sections: Section[];
}>();

const fluent = useFluent();
const sections = computed(() => props.sections);
const type = computed(() => props.type);

function getCountTotal(section: Section) {
    return (
        section.activeVariants.filter((variant) => VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)).length +
        section.nonActiveVariants.filter((variant) => VISIBILITY_TOGGLE_OPTIONS.includes(variant.visibility)).length
    );
}

function defineCategoryColor(category: ExternalEventSeriesCategory | undefined) {
    switch (type.value) {
        case 'Event':
            return 'Event';
        case 'Project':
            return 'Project';
        case 'ExternalEvent':
            return getCategoryColor(category);
    }
}

function getSectionCategoryString(category: ExternalEventSeriesCategory) {
    return fluent.$t('external-event-series-category', { category });
}

defineEmits<{
    close: [];
    toggleExpanded: [id: string];
    toggleVisibility: [toggleValue: VariantStateVisibility, sectionId: string, variantId: string];
    toggleOverrideVisibility: [toggleValue: AllocationVariantOverrideVisibility, sectionId: string, variantId: string];
    makeMainVariantVisible: [];
    makeActiveVariantsVisible: [];
    toggleNonActiveVariants: [sectionId: string];
}>();

const countTotalVariants = computed(() => {
    return sections.value.reduce((sum, section) => {
        return sum + section.activeVariants.length + section.nonActiveVariants.length;
    }, 0);
});
</script>
